/**
 * Credit Notes module — list, search, filter Zoho credit notes.
 * Linked to enrolment number + invoice for reporting.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, RefreshCw, FileText, Receipt, Loader2, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink, ZohoInvoiceLink, ZohoCrmLeadLink, EnrollmentNoCrmLink, ZohoBooksCustomerButton } from '../../components/ZohoLinks';

const fmt = (v) => v == null || v === '' || isNaN(Number(v)) ? '-'
  : Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function CreditNotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const enrollmentParam = searchParams.get('enrollment_no') || '';
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_amount: 0, applied_amount: 0, balance_amount: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(enrollmentParam);
  const [searchInput, setSearchInput] = useState(enrollmentParam);
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('true');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      // Prefer exact enrollment_no match when navigated via URL
      if (enrollmentParam && search === enrollmentParam) {
        params.append('enrollment_no', enrollmentParam);
      } else if (search) {
        params.append('search', search);
      }
      if (statusFilter) params.append('status', statusFilter);
      if (appliedFilter) params.append('has_applied', appliedFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const r = await api.get(`/training/credit-notes?${params}`);
      setItems(r.data.credit_notes || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || { total_amount: 0, applied_amount: 0, balance_amount: 0 });
    } catch (e) { toast.error('Failed to load credit notes'); }
    finally { setLoading(false); }
  }, [page, pageSize, search, enrollmentParam, statusFilter, appliedFilter, dateFrom, dateTo]);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get('/training/credit-notes/sync-status'); setSyncStatus(r.data); } catch { /* ignore */ }
  }, []);

  // When navigated with ?enrollment_no=..., reset other filters so we see all CNs for that student
  useEffect(() => {
    if (enrollmentParam) {
      setSearch(enrollmentParam);
      setSearchInput(enrollmentParam);
      setAppliedFilter('');
      setStatusFilter('');
      setDateFrom('');
      setDateTo('');
      setPage(1);
    }
  }, [enrollmentParam]);

  useEffect(() => { load(); loadStatus(); }, [load, loadStatus]);

  const triggerSync = async () => {
    if (!window.confirm('Sync credit notes from Zoho? This may take 30-60 seconds.')) return;
    setSyncing(true);
    try {
      await api.post('/training/credit-notes/sync');
      toast.info('Sync started — refreshing in 30s');
      setTimeout(async () => { await loadStatus(); await load(); setSyncing(false); toast.success('Sync complete'); }, 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); setSyncing(false); }
  };

  const exportCsv = () => {
    const headers = ['CN Number', 'Date', 'Enrollment No', 'Customer', 'Total', 'Applied', 'Balance', 'Status', 'Applied To Invoices'];
    const rows = items.map(c => [
      c.creditnote_number, c.date, c.enrollment_no || '',
      c.customer_name, c.total, c.credits_applied, c.balance, c.status,
      (c.invoices_credited || []).map(i => `${i.invoice_number}=${i.amount}`).join(' | '),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `credit-notes-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 space-y-4" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Receipt className="h-5 w-5 text-violet-600" /> Credit Notes</h1>
          <p className="text-xs text-slate-500 mt-0.5">Zoho Books credit notes linked to enrolment numbers & invoices</p>
        </div>
        <div className="flex items-center gap-2">
          {syncStatus?.last_synced_at && (
            <span className="text-[10px] text-slate-400">Last sync: {new Date(syncStatus.last_synced_at).toLocaleString()}</span>
          )}
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="cn-export"><Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV</Button>
          <Button onClick={triggerSync} disabled={syncing} size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="cn-sync">
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            {syncing ? 'Syncing...' : 'Sync from Zoho'}
          </Button>
        </div>
      </div>

      {/* Filtered-by-enrollment banner */}
      {enrollmentParam && (
        <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-xs">
          <Receipt className="h-3.5 w-3.5 text-violet-600" />
          <span className="text-violet-900">Showing credit notes for enrollment <b>{enrollmentParam}</b></span>
          <button onClick={() => setSearchParams({})} className="ml-auto text-[10px] text-violet-700 hover:underline" data-testid="cn-clear-enrollment">Clear</button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Credit Notes</div><div className="text-xl font-semibold text-slate-900 mt-1">{total}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Value</div><div className="text-xl font-semibold text-slate-800 mt-1">{fmt(summary.total_amount)}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applied to Invoices</div><div className="text-xl font-semibold text-emerald-700 mt-1">{fmt(summary.applied_amount)}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Open Balance</div><div className="text-xl font-semibold text-amber-700 mt-1">{fmt(summary.balance_amount)}</div></Card>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-6 gap-3 items-end">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Search</label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input data-testid="cn-search" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
                placeholder="CN#, Customer, Enrollment, Reference..." className="pl-8 h-9 text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applied</label>
            <Select value={appliedFilter || 'all'} onValueChange={v => { setAppliedFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="cn-applied-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Applied (linked to invoice)</SelectItem>
                <SelectItem value="false">Open / Unapplied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</label>
            <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="cn-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="closed">Closed (fully applied)</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date From</label>
            <Input data-testid="cn-date-from" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="mt-1 h-9 text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date To</label>
            <Input data-testid="cn-date-to" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="mt-1 h-9 text-xs" />
          </div>
        </div>
      </Card>

      {/* Result table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="cn-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8"></TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">CN Number</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Enrollment No</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Applied</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Balance</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Invoices Linked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-12">No credit notes match the filters</TableCell></TableRow>
              ) : items.map(c => {
                const isExp = expandedId === c.creditnote_id;
                return (
                  <>
                  <TableRow key={c.creditnote_id} className="hover:bg-violet-50/30 cursor-pointer"
                    onClick={() => setExpandedId(isExp ? null : c.creditnote_id)}
                    data-testid={`cn-row-${c.creditnote_id}`}>
                    <TableCell className="w-8 p-2">{isExp ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}</TableCell>
                    <TableCell className="text-xs font-medium text-slate-800">{c.creditnote_number}</TableCell>
                    <TableCell className="text-xs text-slate-600">{c.date}</TableCell>
                    <TableCell className="text-xs">{c.enrollment_no ? <EnrollmentNoCrmLink enrollmentNo={c.enrollment_no} className="text-xs" /> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-xs text-slate-700 truncate max-w-[200px]">{c.customer_name}</TableCell>
                    <TableCell className="text-xs text-right text-slate-700">{fmt(c.total)}</TableCell>
                    <TableCell className="text-xs text-right font-medium text-emerald-700">{fmt(c.credits_applied)}</TableCell>
                    <TableCell className="text-xs text-right text-amber-700">{fmt(c.balance)}</TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell className="text-xs">
                      {(c.invoices_credited || []).length === 0 ? <span className="text-slate-300">—</span> :
                        <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3 text-slate-400" /> {c.invoices_credited.length}</span>}
                    </TableCell>
                  </TableRow>
                  {isExp && (
                    <TableRow key={`${c.creditnote_id}-exp`}>
                      <TableCell colSpan={10} className="bg-slate-50/60 p-4">
                        <CreditNoteDrilldown cn={c} />
                      </TableCell>
                    </TableRow>
                  )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-slate-600">
            <span>Page {page} of {totalPages} • {total} records</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="cn-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="cn-next">Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    open: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-blue-50 text-blue-700 border-blue-200',
    void: 'bg-slate-100 text-slate-500 border-slate-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{status || '—'}</Badge>;
}

function CreditNoteDrilldown({ cn }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Credit Note</div>
        <div className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
          <span className="text-slate-400">Number:</span><span className="font-medium text-slate-800">{cn.creditnote_number}</span>
          <span className="text-slate-400">Date:</span><span className="text-slate-700">{cn.date}</span>
          <span className="text-slate-400">Status:</span><StatusPill status={cn.status} />
          <span className="text-slate-400">Reference:</span><span className="text-slate-700">{cn.reference_number || '—'}</span>
          <span className="text-slate-400">Customer:</span><span className="text-slate-700 truncate">{cn.customer_name}</span>
          {cn.enrollment_no && (<>
            <span className="text-slate-400">Enrollment:</span>
            <span className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-slate-700 text-xs">{cn.enrollment_no}</span>
              <ZohoCrmLeadLink enrollmentNo={cn.enrollment_no} compact />
              <ZohoBooksCustomerButton zohoCustomerId={cn.customer_id} compact />
            </span>
          </>)}
        </div>
      </Card>

      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Amounts</div>
        <div className="grid grid-cols-2 gap-y-1.5 text-xs">
          <span className="text-slate-400">Total:</span><span className="text-right font-medium text-slate-800">{fmt(cn.total)}</span>
          <span className="text-slate-400">Applied:</span><span className="text-right font-medium text-emerald-700">{fmt(cn.credits_applied)}</span>
          <span className="text-slate-400">Open Balance:</span><span className="text-right font-medium text-amber-700">{fmt(cn.balance)}</span>
        </div>
      </Card>

      <Card className="p-3 col-span-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <FileText className="h-3 w-3" /> Linked Invoices ({(cn.invoices_credited || []).length})
        </div>
        {(cn.invoices_credited || []).length === 0 ? (
          <p className="text-xs text-slate-400">Not yet applied to any invoice</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Invoice #</th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applied Date</th>
                <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applied Amount</th>
              </tr>
            </thead>
            <tbody>
              {cn.invoices_credited.map((ic, i) => (
                <tr key={ic.invoice_id || i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1.5"><ZohoInvoiceLink invoiceNumber={ic.invoice_number} invoiceId={ic.invoice_id} className="text-xs" /></td>
                  <td className="px-2 py-1.5 text-slate-600">{ic.date || '—'}</td>
                  <td className="px-2 py-1.5 text-right font-medium text-emerald-700">{fmt(ic.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
